import axios from 'axios';
import API_BASE_URL from '../config/apiBase';

const createResourceCache = () => ({
  data: null,
  promise: null,
});

const airplaneCache = createResourceCache();
const topicListCache = createResourceCache();
const topicDetailCache = new Map();

const readArrayPayload = (payload) => (Array.isArray(payload) ? payload : []);

const fetchIntoCache = async (cache, request) => {
  if (cache.data) {
    return cache.data;
  }

  if (cache.promise) {
    return cache.promise;
  }

  cache.promise = request()
    .then((data) => {
      cache.data = data;
      return data;
    })
    .finally(() => {
      cache.promise = null;
    });

  return cache.promise;
};

export const getCachedAirplanes = () => airplaneCache.data;

export const fetchAirplanes = async () =>
  fetchIntoCache(airplaneCache, async () => {
    const response = await axios.get(`${API_BASE_URL}/api/airplanes`);
    return readArrayPayload(response.data);
  });

export const getCachedTopics = () => topicListCache.data;

export const fetchTopics = async () =>
  fetchIntoCache(topicListCache, async () => {
    const response = await axios.get(`${API_BASE_URL}/api/topics`);
    return readArrayPayload(response.data);
  });

export const getCachedTopicDetail = (topicId) => {
  if (!topicId) {
    return null;
  }

  return topicDetailCache.get(String(topicId))?.data ?? null;
};

export const fetchTopicDetail = async (topicId) => {
  const cacheKey = String(topicId);
  const cachedEntry = topicDetailCache.get(cacheKey);

  if (cachedEntry?.data) {
    return cachedEntry.data;
  }

  if (cachedEntry?.promise) {
    return cachedEntry.promise;
  }

  const promise = axios
    .get(`${API_BASE_URL}/api/topics/${topicId}`)
    .then((response) => {
      const topic = response.data ?? null;
      topicDetailCache.set(cacheKey, {
        data: topic,
        promise: null,
      });
      return topic;
    })
    .catch((error) => {
      topicDetailCache.delete(cacheKey);
      throw error;
    });

  topicDetailCache.set(cacheKey, {
    data: cachedEntry?.data ?? null,
    promise,
  });

  return promise;
};

export const prefetchTopicDetail = (topicId) => {
  if (!topicId) {
    return;
  }

  void fetchTopicDetail(topicId).catch(() => {});
};
